import { useRef, useCallback } from 'react'
import { Camera, Loader2 } from '@/ui/icons'
import { Button } from '@/ui/design-system'
import { SectionCard } from './SectionCard'
import { usePhotos } from '@/modules/photos/hooks/usePhotos'
import { PhotoGallery } from '@/modules/photos/components/PhotoGallery'
import { useSubscription } from '@/modules/subscriptions/hooks/useSubscription'
import { useAuth } from '@/modules/auth'
import { createApiClient } from '@/shared/api/client'

function formatBytes(bytes: number): string {
  if (bytes >= 1_073_741_824) return `${(bytes / 1_073_741_824).toFixed(1)} GB`
  if (bytes >= 1_048_576) return `${(bytes / 1_048_576).toFixed(0)} MB`
  return `${Math.round(bytes / 1_024)} KB`
}

interface TripGallerySectionProps {
  tripId: string
  coverPhotoId?: string | null
  canEdit?: boolean
  onCoverUpdate?: () => void
}

export function TripGallerySection({ tripId, coverPhotoId, canEdit, onCoverUpdate }: TripGallerySectionProps) {
  const { photos, isLoading, isUploading, isUpdating, isDeleting, createPhoto, updatePhoto, deletePhoto } = usePhotos(tripId)
  const { storageStatus } = useSubscription()
  const { getAccessToken } = useAuth()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? [])
      if (files.length === 0) return
      for (const file of files) {
        await createPhoto({ photo: file })
      }
      e.target.value = ''
    },
    [createPhoto]
  )

  const handleSetCoverPhoto = useCallback(
    async (photoId: string) => {
      const apiClient = createApiClient(getAccessToken)
      await apiClient.put(`/trips/${tripId}/cover-photo/${photoId}`)
      onCoverUpdate?.()
    },
    [tripId, getAccessToken, onCoverUpdate]
  )

  const isAtQuota = storageStatus ? storageStatus.remainingBytes === 0 : false

  const uploadAction = canEdit ? (
    <div className="flex items-center gap-2">
      {storageStatus && (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <div className="w-16 h-1 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full ${
                storageStatus.usedPercent >= 100 ? 'bg-red-500' :
                storageStatus.usedPercent >= 80 ? 'bg-amber-500' :
                'bg-adventure-600'
              }`}
              style={{ width: `${storageStatus.usedPercent}%` }}
            />
          </div>
          <span>{formatBytes(storageStatus.remainingBytes)} left</span>
        </div>
      )}
      <Button
        size="sm"
        variant="outline"
        className="gap-1.5"
        onClick={() => fileInputRef.current?.click()}
        disabled={isUploading || isAtQuota}
        title={isAtQuota ? 'Storage full — delete photos or upgrade to Pro' : undefined}
      >
        {isUploading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Camera className="h-4 w-4" aria-hidden />}
        {isUploading ? 'Uploading…' : 'Add Photos'}
      </Button>
    </div>
  ) : undefined

  return (
    <>
      {canEdit && (
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          className="hidden"
          onChange={handleFileSelect}
          disabled={isUploading}
        />
      )}

      <SectionCard title="Photos" count={photos.length > 0 ? photos.length : undefined} actions={uploadAction}>
        {isLoading ? (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="aspect-square rounded-lg bg-earth-100 dark:bg-earth-800 animate-pulse" />
            ))}
          </div>
        ) : photos.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center gap-3">
            <div className="h-12 w-12 rounded-full bg-earth-100 dark:bg-earth-800 flex items-center justify-center">
              <Camera className="h-6 w-6 text-earth-400" aria-hidden />
            </div>
            <div>
              <p className="text-sm font-medium text-earth-700 dark:text-earth-300">No photos yet</p>
              <p className="text-xs text-earth-500 dark:text-earth-500 mt-0.5">
                Add photos from this trip to share with your crew
              </p>
            </div>
            {canEdit && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="gap-1.5"
              >
                <Camera className="h-4 w-4" aria-hidden />
                Upload Photos
              </Button>
            )}
          </div>
        ) : (
          <PhotoGallery
            photos={photos}
            tripId={tripId}
            coverPhotoId={coverPhotoId}
            onUpdatePhoto={updatePhoto}
            onDeletePhoto={deletePhoto}
            onSetCoverPhoto={canEdit ? handleSetCoverPhoto : undefined}
            isUpdating={isUpdating}
            isDeleting={isDeleting}
          />
        )}
      </SectionCard>
    </>
  )
}
